// frontend/src/components/common/ListaUnificada.jsx
// COMPONENTE LISTA UNIFICADO - PLANTILLA BASE PARA TODAS LAS PÁGINAS
// RESUELVE TODOS LOS PROBLEMAS: paginación, filtros, búsqueda, ordenamiento
import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import styled, { useTheme } from 'styled-components';
import { ActionButton as SharedActionButton } from './Buttons';
import Pagination from './Pagination.jsx';
import Modal from './Modal.jsx';
import { useToast } from './Toast';
import DynamicFilterBuilder from './DynamicFilterBuilder.jsx';
import SearchableSelectField from './SearchableSelectField.jsx';
import apiClient from '../../services/apiClient.js';
import uiPreferencesService from '../../services/uiPreferencesService.js';
import HelpPanel, { HelpToggleBtn } from './HelpPanel';
import { SeccionBar, SeccionTitulo, SeccionAcciones, SeccionBuscador,
  ESTILO_BOTON_SECCION } from './ModuloHeader.jsx';
import { OutlinedButton } from './Buttons';

// Utility to debounce frequent calls
const debounce = (func, wait) => {
  let timeout;
  const debounced = function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  return debounced;
};

// ============================= ESTILOS GLOBALES =============================

const ListaContainer = styled.div`
  /* Ajustado para ocupar toda la altura del contenedor padre */
  padding: 4px 6px; /* Reducido de 8px 12px para mayor aprovechamiento */
  background: ${({ theme }) => theme.colors.bgPrincipal};
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 0.25rem; /* Reducido de 0.5rem */
  height: 100%; /* Ocupa toda la altura disponible */
  border: ${({ $sinMarco, theme }) => $sinMarco ? 'none' : `1px solid ${theme.colors.borderDefault}`};
  border-radius: ${({ $sinMarco, theme }) => $sinMarco ? '0' : theme.borderRadius.md};

  /* Variant "rows": misma superficie continua que Check List — la barra de
     título y la tabla llegan al borde del panel, sin recuadros anidados. */
  ${({ $variant }) => $variant === 'rows' && `
    padding: 0;
    gap: 0;
  `}
`;

const HeaderBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem; /* Reducido para menos espacio */
  gap: 0.25rem; /* Reducido de 0.5rem */
  flex-shrink: 0; /* No se contraiga */

  ${({ $variant, theme }) => $variant === 'rows' && `
    margin-bottom: 0;
    padding: 0.55rem 0.75rem;
    background: ${theme.colors.bgSecundario};
    border-bottom: 1px solid ${theme.colors.borderDefault};
  `}
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageTitle = styled.h5`
  margin: 0;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};

  i {
    color: ${({ theme }) => theme.colors.info};
    margin-right: 0.5rem;
  }

  ${({ $variant, $tituloGrande }) => $variant === 'rows' && !$tituloGrande && `
    font-size: 0.83rem;
    font-weight: 700;
  `}

  ${({ $variant, $tituloGrande }) => $variant === 'rows' && $tituloGrande && `
    font-size: 1rem;
    font-weight: 700;
  `}
`;

const Subtitle = styled.small`
  color: ${({ theme }) => theme.colors.txtSecondary};
  margin-left: 0.5rem;
  font-weight: normal;
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

// ============================= BÚSQUEDA Y FILTROS =============================

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const SearchInput = styled.input`
  padding: 0.45rem 2.2rem 0.45rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 200px; /* más compacto */
  transition: ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
  border-color: ${({ theme }) => theme.colors.info};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.shadowFocus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.txtMuted};
  }

  ${({ $variant }) => $variant === 'rows' && `
    padding: 0.35rem 2rem 0.35rem 0.7rem;
    border-radius: 5px;
    font-size: 0.78rem;
    min-width: 180px;
  `}
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 10px;
  color: ${({ theme }) => theme.colors.txtMuted};
  pointer-events: none;
  
  i {
    font-size: 0.9rem;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 30px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.txtMuted};
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, theme }) => 
  $active ? theme.colors.info : theme.colors.surface
  };
  color: ${({ $active, theme }) => 
    $active ? theme.colors.onPrimary : theme.colors.txtPrincipal
  };
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
  background: ${({ theme }) => theme.colors.infoHover};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const FiltersPanel = styled.div`
  display: ${({ $show }) => $show ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 1000;
  min-width: 300px;
  margin-top: 0.5rem;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  min-width: 80px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const FilterInput = styled.input`
  flex: 1;
  min-width: 200px; /* Ancho mínimo más amplio */
  padding: 0.4rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  color-scheme: ${({ theme }) => (theme.isDark ? 'dark' : 'light')};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
  border-color: ${({ theme }) => theme.colors.info};
  }
`;

// ============================= TABLA =============================

const TableContainer = styled.div`
  flex: 1;
  overflow: auto; /* Permitir scroll cuando sea necesario */
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Permitir que flex funcione correctamente */
  padding-bottom: 0px;

  /* Variant "rows": sin recuadro propio — la separación con la barra de
     título ya la da el border-bottom de esta, igual que Check List. */
  ${({ $variant }) => $variant === 'rows' && `
    border: none;
    border-radius: 0;
  `}
`;

const Table = styled.table`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth ? `${$minWidth}px` : 'auto'};
  border-collapse: collapse;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.surface};
  table-layout: fixed; /* Ayuda a mantener columnas consistentes */

  /* Variant "rows": misma superficie que el resto del panel (bgPrincipal),
     no una "surface" elevada — igual que Check List. */
  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
  `}
`;

const TableHeader = styled.thead`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  position: sticky; /* Header fijo */
  top: 0;
  z-index: 10;
  display: table-header-group; /* Asegurar comportamiento correcto */

  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
  `}
`;

const HeaderCell = styled.th`
  background: ${({ theme }) => theme.colors.bgSecundario};
  padding: 12px 12px; /* Padding un poco mayor para headers */
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: ${({ $sortable }) => $sortable ? 'pointer' : 'default'};
  user-select: none;
  position: relative;
  border-right: ${({ $variant, theme }) => ($variant === 'rows' ? 'none' : `1px solid ${theme.colors.borderLight}`)};
  width: ${({ $width }) => $width || 'auto'};
  min-width: 80px; /* Ancho mínimo */
  overflow: hidden;
  height: 50px; /* Altura fija para headers */
  vertical-align: middle; /* Centrar contenido verticalmente */

  &:hover {
    background: ${({ $sortable, theme }) =>
      $sortable ? theme.colors.surfaceActive : 'inherit'
    };
  }

  &:last-child {
    border-right: none;
  }

  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
    height: auto;
    padding: 6px 10px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 11px;
    font-weight: 700;
    color: ${theme.colors.txtMuted};

    &:hover {
      background: ${theme.colors.bgPrincipal};
    }
  `}
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
`;

const SortIcon = styled.span`
  margin-left: 0.25rem;
  color: ${({ $active, theme }) => 
  $active ? theme.colors.info : theme.colors.txtMuted
  };
  font-size: 0.75rem;
`;

const TableBody = styled.tbody`
  display: table-row-group; /* Comportamiento normal de tbody */
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  height: 45px;

  &:nth-child(even) {
    background: ${({ theme }) => theme.colors.rowAlternate || theme.colors.surfaceHover};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.hoverRow || theme.colors.surfaceHover};
  }

  &:last-child {
    border-bottom: none;
  }

  ${({ $variant, theme }) => $variant === 'rows' && `
    height: auto;
    transition-property: background-color;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;

    &:nth-child(even) {
      background: transparent;
    }

    &:hover {
      background: ${theme.colors.surfaceHover};
    }
  `}
`;

const TableCell = styled.td`
  padding: 8px 12px; /* Padding ajustado para altura fija */
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-right: ${({ $variant, theme }) => ($variant === 'rows' ? 'none' : `1px solid ${theme.colors.borderLight}`)};
  line-height: 1.3; /* Line height ajustado para altura fija */
  white-space: nowrap; /* Evitar saltos de línea */
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle; /* Centrar contenido verticalmente */
  height: 45px;

  &:last-child {
    border-right: none;
  }

  ${({ $variant }) => $variant === 'rows' && `
    height: auto;
    padding: 7px 10px;
    font-size: 12px;
  `}
`;

// ============================= BOTONES DE ACCIÓN EN FILAS =============================

const RowActionsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;

  tr:hover &, tr:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`;

const RowActionButton = styled.button`
  padding: 0.15rem;
  width: 34px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ $variant, theme }) =>
    $variant === 'edit' ? theme.colors.warning :
    $variant === 'delete' ? theme.colors.danger :
    $variant === 'view' ? theme.colors.info : theme.colors.secondary
  };
  font-size: 16px;
  cursor: pointer;
  transition: all 0.12s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ $variant, theme }) =>
      $variant === 'edit' ? theme.colors.warningHover :
      $variant === 'delete' ? theme.colors.dangerHover :
      $variant === 'view' ? theme.colors.infoHover : theme.colors.secondaryHover
    };
    transform: scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  ${({ $tableVariant, $variant, theme }) => $tableVariant === 'rows' && `
    width: 28px;
    height: 28px;
    font-size: 13px;
    border-radius: 6px;
    border-color: ${
      $variant === 'edit' ? theme.colors.warningSoftBorder :
      $variant === 'delete' ? theme.colors.dangerAlphaHover :
      $variant === 'view' ? theme.colors.primarySoftBorder : theme.colors.borderDefault
    };
    background: ${
      $variant === 'edit' ? theme.colors.warningAlpha :
      $variant === 'delete' ? theme.colors.dangerAlpha :
      $variant === 'view' ? theme.colors.primaryAlpha : theme.colors.bgSecundario
    };
    transition-property: background-color, color, border-color;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;

    &:hover {
      transform: none;
      box-shadow: none;
      background: ${
        $variant === 'edit' ? theme.colors.warningSoftBorder :
        $variant === 'delete' ? theme.colors.dangerAlphaHover :
        $variant === 'view' ? theme.colors.primarySoftBorder : theme.colors.surfaceHover
      };
    }
  `}
`;

// Reuse shared ActionButton for consistent variants
const ActionButton = SharedActionButton;

// Botón ghost compacto para variant "rows" — mismo lenguaje que "Nuevo" en Check List
// (CLNuevoBtn de DetalleEquipo.jsx). Se usa en lugar del ActionButton sólido para no
// tocar ese componente compartido, que sigue igual en el resto de la app.
const RowsGhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition-property: background-color, border-color, color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgSecundario};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  i {
    font-size: 0.75rem;
  }
`;

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
};

// ============================= COMPONENTE PRINCIPAL =============================

const ListaUnificada = forwardRef(({
  // Configuración básica
  titulo,
  subtitle, // Subtítulo opcional
  icono = 'fas fa-list',
  help = null, // { prereqs, tips, nextSteps } — muestra el botón de ayuda junto al título
  emptyState = null, // ReactNode opcional para el caso "sin datos" (solo sin búsqueda/filtros activos)
  sinMarco = false,
  endpoint, // Endpoint de la API (ej: 'partes-diarios/')
  
  // Configuración de columnas
  columns = [], // Array de objetos con { key, label, sortable, width, render }
  
  // Configuración de filtros
  filterConfig = [], // Array de objetos con { key, label, type, placeholder }
  
  // Manejadores de eventos
  onRowClick = null,
  onAdd = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  
  // Configuración adicional
  itemsPerPageOptions = [10, 25, 50, 100],
  defaultItemsPerPage = 25,
  searchPlaceholder = 'Buscar...',
  allowAdd = true,
  allowEdit = true,
  allowDelete = true,
  allowView = true,
  actionsColumnWidth = '110px', // Ajustable cuando la lista tiene menos de 3 acciones por fila
  resizableColumns = false,
  tableVisualVariant = 'grid', // 'grid' (clásica) | 'rows' (filas diferenciadas)
  tituloGrande = false, // true = título general de pestaña (más grande que el título compacto por defecto de variant "rows")
  stateStorageKey = null,
  cloudScope = null, // Si se pasa, persiste filtros/búsqueda/orden en el servidor por usuario+tenant
  useAdvancedFilters = false,
  // true = el encabezado de la lista se dibuja como la barra de sección del
  // sistema (título + buscador + filtros + acciones dentro de un recuadro).
  // El título del módulo, su descripción y la ayuda van arriba, en <ModuloHeader>.
  barraSeccion = false,
  textoBotonAgregar = 'Agregar', // Texto del botón de alta en la barra de sección

  // Props adicionales para personalización
  additionalActions = [], // Array de botones adicionales
  ...props
}, ref) => {
  const theme = useTheme();
  const { toast } = useToast();
  const cloudHydratedRef = useRef(false);

  const loadSavedTableState = () => {
    if (!stateStorageKey) return null;
    try {
      const saved = localStorage.getItem(stateStorageKey);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const savedTableState = loadSavedTableState();
  // ============================= ESTADOS =============================

  // Cuando hay cloudScope, el fetch inicial espera a que se restauren las preferencias del servidor.
  // Sin cloudScope arranca listo de inmediato.
  const [cloudReady, setCloudReady] = useState(!cloudScope);

  const [data, setData] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados de búsqueda y filtros
  const [searchValue, setSearchValue] = useState(savedTableState?.searchValue || '');
  const [filters, setFilters] = useState(savedTableState?.filters || {});
  const [showFilters, setShowFilters] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [dynamicFilters, setDynamicFilters] = useState({
    columnFilters: {},
    touchedFilters: {},
    expandedFilterGroups: {},
  });
  
  // Estados de paginación y ordenamiento
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(savedTableState?.itemsPerPage || defaultItemsPerPage);
  const [sortColumn, setSortColumn] = useState(savedTableState?.sortColumn || null);
  const [sortDirection, setSortDirection] = useState(savedTableState?.sortDirection || 'asc');
  const [columnWidths, setColumnWidths] = useState(savedTableState?.columnWidths || {});
  
  // Referencias para debounce separadas para evitar conflictos
  const searchTimeoutRef = useRef(null);
  const filtersTimeoutRef = useRef(null);
  const filtersRef = useRef(null);
  const userChangedItemsPerPageRef = useRef(false); // Track if user manually changed items per page
  // La carga inicial la dispara el efecto de montaje. Los efectos de búsqueda y
  // filtros corren también en esa primera vuelta (por su dependencia de
  // cloudReady) y, al vencer su debounce, relanzaban la MISMA petición 1 s
  // después: cada listado pedía sus datos dos veces. Estos refs saltean esa
  // primera corrida; a partir del segundo cambio, el usuario ya tocó algo y el
  // refetch es real.
  const busquedaInicialOmitidaRef = useRef(false);
  const filtrosInicialOmitidaRef = useRef(false);
  // Refs to keep latest state values stable for the debounced fetch
  const stateRefs = useRef({
    currentPage,
    itemsPerPage,
    searchValue,
    filters,
    sortColumn,
    sortDirection
  });
  const resizingRef = useRef(null);

  // Helper to create readable labels from object keys
  const humanize = (key) => {
    if (!key) return '';
    return String(key)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // If no columns were provided, infer them from the first data item (non-invasive fallback)
  // Filter out keys that are likely FK id fields (ending with _id) or internal ids of related objects
  const inferColumnsFromItem = (item) => {
    if (!item || typeof item !== 'object') return [];
    return Object.keys(item).filter(k => {
      // Skip raw foreign key id fields (e.g., usuario_id, equipo_id)
      if (/(_id$|^id$)/i.test(k)) return false;
      // Skip fields that are metadata or internal
      if (k.startsWith('_') || k === 'url') return false;
      return true;
    }).map(k => ({ key: k, label: humanize(k) }));
  };

  const effectiveColumns = useMemo(
    () => (columns && columns.length) ? columns : (data && data.length ? inferColumnsFromItem(data[0]) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, data]
  );

  // Ancho mínimo total de la tabla: suma del ancho de cada columna (el explícito,
  // el ya redimensionado por el usuario, o el mínimo de 80px que fuerza HeaderCell)
  // más la columna de acciones. Al fijarlo como min-width de <table>, cuando las
  // columnas no entran en el contenedor la tabla no se comprime — crece y dispara
  // el scroll horizontal de TableContainer en vez de tapar columnas al final.
  const COLUMN_MIN_WIDTH = 80;
  const parseWidthPx = (value, fallback) => {
    if (!value) return fallback;
    const parsed = parseInt(String(value).replace('px', ''), 10);
    return Number.isFinite(parsed) ? Math.max(parsed, COLUMN_MIN_WIDTH) : fallback;
  };
  const tableMinWidth = useMemo(() => {
    const columnsWidth = effectiveColumns.reduce((sum, column) => {
      const width = columnWidths[column.key] || column.width;
      return sum + parseWidthPx(width, COLUMN_MIN_WIDTH);
    }, 0);
    const actionsWidth = (allowView || allowEdit || allowDelete)
      ? parseWidthPx(actionsColumnWidth, COLUMN_MIN_WIDTH)
      : 0;
    return columnsWidth + actionsWidth;
  }, [effectiveColumns, columnWidths, actionsColumnWidth, allowView, allowEdit, allowDelete]);

  const advancedFilterColumns = useMemo(() => {
    if (!useAdvancedFilters) return [];
    return (filterConfig || [])
      .filter((f) => f && f.type !== 'date')
      .map((f) => {
        const sourceKey = f.sourceKey || String(f.key || '')
          .replace(/__(icontains|contains|iexact|exact|in|gte|lte)$/i, '')
          .replace(/__/g, '_');
        return {
          key: f.key,
          label: f.label,
          getValue: (row) => {
            const raw = row?.[sourceKey];
            if (raw === null || raw === undefined) return '';
            return String(raw).trim();
          },
        };
      });
  }, [filterConfig, useAdvancedFilters]);

  const rowsToRender = useMemo(() => {
    if (!useAdvancedFilters) return data;
    return (data || []).filter((row) => {
      for (const col of advancedFilterColumns) {
        if (!dynamicFilters.touchedFilters?.[col.key]) continue;
        const selected = ensureArray(dynamicFilters.columnFilters?.[col.key]);
        const valueText = col.getValue ? col.getValue(row) : '';
        if (!selected.length || !selected.includes(valueText)) return false;
      }
      return true;
    });
  }, [advancedFilterColumns, data, dynamicFilters, useAdvancedFilters]);

  // Single debounced fetch stored in a ref to avoid recreation on every render
  const debouncedFetchRef = useRef(null);
  
  // Keep refs up to date when relevant state changes
  useEffect(() => {
    stateRefs.current.currentPage = currentPage;
    stateRefs.current.itemsPerPage = itemsPerPage;
    stateRefs.current.searchValue = searchValue;
    stateRefs.current.filters = filters;
    stateRefs.current.sortColumn = sortColumn;
    stateRefs.current.sortDirection = sortDirection;
  }, [currentPage, itemsPerPage, searchValue, filters, sortColumn, sortDirection]);

  useEffect(() => {
    if (!stateStorageKey) return;
    try {
      localStorage.setItem(stateStorageKey, JSON.stringify({
        searchValue,
        filters,
        itemsPerPage,
        sortColumn,
        sortDirection,
        columnWidths,
      }));
    } catch {
      // Ignorar errores de persistencia
    }
  }, [stateStorageKey, searchValue, filters, itemsPerPage, sortColumn, sortDirection, columnWidths]);

  // Hidratación inicial desde servidor
  useEffect(() => {
    if (!cloudScope) {
      cloudHydratedRef.current = true;
      return;
    }
    let active = true;
    cloudHydratedRef.current = false;

    const aplicar = (parsed) => {
      if (!parsed || typeof parsed !== 'object') return;
      if (typeof parsed.searchValue === 'string') setSearchValue(parsed.searchValue);
      if (parsed.filters && typeof parsed.filters === 'object') setFilters(parsed.filters);
      if (typeof parsed.itemsPerPage === 'number') setItemsPerPage(parsed.itemsPerPage);
      if (typeof parsed.sortColumn === 'string') setSortColumn(parsed.sortColumn);
      if (parsed.sortDirection === 'asc' || parsed.sortDirection === 'desc') setSortDirection(parsed.sortDirection);
    };

    // `getScopeRapido` resuelve al instante si ya conocíamos el scope, y avisa
    // por callback sólo cuando el servidor trae algo distinto. Así el fetch de
    // datos deja de esperar un ida y vuelta que casi siempre devuelve lo mismo.
    uiPreferencesService.getScopeRapido(cloudScope, (fresco) => {
      if (!active) return;
      aplicar(fresco);
    }).then(({ value }) => {
      if (!active) return;
      aplicar(value);
    }).catch((err) => {
      console.warn(`[cloudScope] GET error ${cloudScope}:`, err?.response?.status, err?.message);
    }).finally(() => {
      if (active) {
        cloudHydratedRef.current = true;
        setCloudReady(true);
      }
    });
    return () => { active = false; };
  // Solo al montar (cloudScope no cambia en runtime)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudScope]);

  // Guardado en servidor con debounce 600ms — solo después de hidratar
  useEffect(() => {
    if (!cloudScope || !cloudHydratedRef.current) return;
    const timeoutId = setTimeout(() => {
      const payload = { searchValue, filters, itemsPerPage, sortColumn, sortDirection };
      console.log(`[cloudScope] SET ${cloudScope}:`, payload);
      uiPreferencesService.setScope(cloudScope, payload).catch((err) => {
        console.warn(`[cloudScope] SET error ${cloudScope}:`, err?.response?.status, err?.message);
      });
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [cloudScope, searchValue, filters, itemsPerPage, sortColumn, sortDirection]);

  // Core fetch reads latest values from refs to avoid changing identity on every state update
  const fetchDataCore = useCallback((params = {}) => {
    if (!endpoint) {
      setError('Endpoint no configurado');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const s = stateRefs.current;

    // Construir parámetros de consulta usando los refs
    const queryParams = {
      page: s.currentPage,
      page_size: s.itemsPerPage,
      search: s.searchValue || undefined,
      ordering: s.sortColumn && s.sortDirection ? `${s.sortDirection === 'desc' ? '-' : ''}${s.sortColumn}` : undefined,
      ...s.filters,
      ...params
    };

    // Filtrar parámetros vacíos
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined || queryParams[key] === '') {
        delete queryParams[key];
      }
    });

    apiClient.get(endpoint, { params: queryParams })
      .then(response => {
        if (response.data.results) {
          // Respuesta paginada de DRF
          setData(response.data.results);
          setTotalItems(response.data.count);
          setTotalPages(Math.ceil(response.data.count / s.itemsPerPage));
        } else {
          // Respuesta simple (array)
          setData(response.data);
          setTotalItems(response.data.length);
          setTotalPages(1);
        }
      })
      .catch(err => {
        console.error('❌ ListaUnificada - Error cargando datos:', err);
        setError(err.response?.data?.detail || err.message || 'Error cargando datos');
        setData([]);
        setTotalItems(0);
        setTotalPages(1);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [endpoint]);

  // Initialize single debounced fetch function and keep it when possible
  useEffect(() => {
    // Cancel existing when endpoint changes
    if (debouncedFetchRef.current && debouncedFetchRef.current.cancel) {
      debouncedFetchRef.current.cancel();
    }

    debouncedFetchRef.current = debounce(fetchDataCore, 300);

    return () => {
      if (debouncedFetchRef.current && debouncedFetchRef.current.cancel) debouncedFetchRef.current.cancel();
    };
  }, [fetchDataCore]);
  
  // Expose refresh method to parent components
  useImperativeHandle(ref, () => ({
    refresh: (params = {}) => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current({ ...params, _refresh: Date.now() });
      } else {
        fetchDataCore({ ...params, _refresh: Date.now() });
      }
    },
    refreshImmediate: (params = {}) => {
      if (debouncedFetchRef.current && debouncedFetchRef.current.cancel) {
        debouncedFetchRef.current.cancel();
      }
      // Call core fetch immediately
      fetchDataCore({ ...params, _refresh: Date.now() });
    }
  }), [fetchDataCore]);
  
  // ============================= FUNCIONES DE DATOS =============================
  
  // Función para cargar datos desde la API (uses the shared debounced fetch when available)
  const fetchData = useCallback((params = {}) => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current(params);
    } else {
      // Fallback: call core directly
      fetchDataCore(params);
    }
  }, [fetchDataCore]);
  
  // ============================= EFECTOS =============================
  
  // Sincronizar itemsPerPage cuando cambie defaultItemsPerPage (solo si no fue cambiado manualmente)
  useEffect(() => {
    if (defaultItemsPerPage && !userChangedItemsPerPageRef.current) {
      setItemsPerPage(defaultItemsPerPage);
    }
  }, [defaultItemsPerPage]);
  
  // Cargar datos inicial y cuando cambien las dependencias.
  // Si hay cloudScope, espera a que la hidratación de preferencias termine antes de fetchear.
  useEffect(() => {
    if (!cloudReady) return;
    fetchData();

    const onRecordSaved = (e) => {
      const info = e.detail || {};
      const matches = !info.endpoint || (info.endpoint && endpoint &&
        info.endpoint.replace(/\/+$/, '') === endpoint.replace(/\/+$/, ''));
      if (matches) {
        fetchData({ _refresh: Date.now() });
      }
    };

    window.addEventListener('gestion-taller:record-saved', onRecordSaved);

    return () => {
      window.removeEventListener('gestion-taller:record-saved', onRecordSaved);
    };
  }, [fetchData, endpoint, cloudReady]);

  // Fetch data when pagination changes
  useEffect(() => {
    if (!cloudReady) return;
    fetchData();
  }, [currentPage, itemsPerPage, fetchData, cloudReady]);
  
  // Efecto para búsqueda con debounce más largo para evitar flickering
  useEffect(() => {
    if (!cloudReady) return;
    if (!busquedaInicialOmitidaRef.current) {
      // Primera vuelta: el valor de búsqueda es el que ya se hidrató y el fetch
      // inicial lo incluyó. Refetchearlo sería pedir dos veces lo mismo.
      busquedaInicialOmitidaRef.current = true;
      return undefined;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // Reset a página 1 al buscar
      } else {
        fetchData();
      }
    }, 1000); // Debounce más largo para búsqueda: 1000ms para evitar flickering

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, cloudReady]);

  // Efecto para filtros con debounce separado para evitar flickering
  useEffect(() => {
    if (!cloudReady) return;
    if (!filtrosInicialOmitidaRef.current) {
      // Misma razón que en búsqueda: los filtros hidratados ya viajaron en el
      // fetch inicial.
      filtrosInicialOmitidaRef.current = true;
      return undefined;
    }
    if (filtersTimeoutRef.current) {
      clearTimeout(filtersTimeoutRef.current);
    }

    filtersTimeoutRef.current = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // Reset a página 1 al filtrar
      } else {
        fetchData();
      }
    }, 1000); // Debounce más largo para filtros: 1000ms

    return () => {
      if (filtersTimeoutRef.current) {
        clearTimeout(filtersTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, cloudReady]);

  // Efecto para ordenamiento: relanza la petición al cambiar columna o dirección
  useEffect(() => {
    if (!cloudReady) return;
    if (sortColumn === null) return; // Ignorar estado inicial (sin ordenamiento activo)
    if (currentPage !== 1) {
      setCurrentPage(1); // Reset a página 1; el cambio de página ya dispara el fetch
    } else {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection, cloudReady]);
  
  // ============================= MANEJADORES DE EVENTOS =============================
  
  const handleSearchChange = (value) => {
    console.log('🔍 ListaUnificada - Búsqueda:', value);
    setSearchValue(value);
  };
  
  const handleFilterChange = (fieldName, value) => {
    console.log('🔍 ListaUnificada - Filtro cambiado:', fieldName, value);
    setFilters(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  const handleClearFilters = () => {
    setFilters({});
    setSearchValue('');
    if (useAdvancedFilters) {
      setDynamicFilters({ columnFilters: {}, touchedFilters: {}, expandedFilterGroups: {} });
    }
    if (cloudScope) {
      uiPreferencesService.clearScope(cloudScope).catch(() => {});
    }
  };
  
  const handleSort = (column) => {
    console.log('🔄 ListaUnificada - Ordenamiento:', column);
    if (!column.sortable) return;
    
    const columnKey = column.sortKey || column.key;
    
    if (sortColumn === columnKey) {
      // Cambiar dirección si es la misma columna
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nueva columna, empezar con asc
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handleItemsPerPageChange = (newItemsPerPage) => {
    userChangedItemsPerPageRef.current = true; // Mark that user manually changed the value
    setItemsPerPage(parseInt(newItemsPerPage));
    setCurrentPage(1); // Reset a página 1
    // Trigger immediate fetch with new items per page
    if (debouncedFetchRef.current && debouncedFetchRef.current.cancel) {
      debouncedFetchRef.current.cancel();
    }
    // Force immediate refetch
    setTimeout(() => {
      fetchDataCore({ _refresh: Date.now() });
    }, 50);
  };
  
  const handleRowClick = (item, index) => {
    if (onRowClick) {
      onRowClick(item, index);
    }
  };
  
  // ============================= FUNCIONES DE RENDERIZADO =============================
  
  const renderTableHeader = () => (
    <TableHeader $variant={tableVisualVariant}>
      <tr>
  {effectiveColumns.map((column, index) => (
          <HeaderCell
            key={column.key || index}
            $sortable={column.sortable}
            $width={columnWidths[column.key] || column.width}
            $variant={tableVisualVariant}
            onClick={() => handleSort(column)}
          >
            {column.label}
            {column.sortable && (
              <SortIcon 
                $active={sortColumn === (column.sortKey || column.key)}
              >
                {sortColumn === (column.sortKey || column.key) ? 
                  (sortDirection === 'asc' ? '↑' : '↓') : '↕'
                }
              </SortIcon>
            )}
            {resizableColumns && column.key ? (
              <ResizeHandle
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const columnKey = column.key;
                  const startX = event.clientX;
                  const currentWidth = columnWidths[columnKey]
                    ? parseInt(String(columnWidths[columnKey]).replace('px', ''), 10)
                    : (event.currentTarget.parentElement?.offsetWidth || 120);

                  resizingRef.current = {
                    key: columnKey,
                    startX,
                    startWidth: Number.isFinite(currentWidth) ? currentWidth : 120,
                  };

                  const onMouseMove = (moveEvent) => {
                    const active = resizingRef.current;
                    if (!active || !active.key) return;
                    const deltaX = moveEvent.clientX - active.startX;
                    const nextWidth = Math.max(80, active.startWidth + deltaX);
                    const keyToResize = active.key;
                    setColumnWidths((prev) => ({ ...prev, [keyToResize]: `${nextWidth}px` }));
                  };

                  const onMouseUp = () => {
                    document.removeEventListener('mousemove', onMouseMove);
                    document.removeEventListener('mouseup', onMouseUp);
                    resizingRef.current = null;
                  };

                  document.addEventListener('mousemove', onMouseMove);
                  document.addEventListener('mouseup', onMouseUp);
                }}
              />
            ) : null}
          </HeaderCell>
        ))}
        {/* Columna de acciones si están habilitadas */}
        {(allowView || allowEdit || allowDelete) && (
          <HeaderCell $width={actionsColumnWidth} $variant={tableVisualVariant} style={{ textAlign: 'center' }}>
            Acciones
          </HeaderCell>
        )}
      </tr>
    </TableHeader>
  );
  
  const renderTableBody = () => (
    <TableBody>
      {rowsToRender.map((item, index) => (
        <TableRow
          key={item.id || index}
          $clickable={!!onRowClick}
          $variant={tableVisualVariant}
          onClick={() => handleRowClick(item, index)}
        >
          {effectiveColumns.map((column, colIndex) => (
            <TableCell
              key={column.key || colIndex}
              $variant={tableVisualVariant}
              title={(() => {
                if (typeof column.cellTitle === 'function') {
                  const v = column.cellTitle(item[column.key], item, index);
                  return v == null ? '' : String(v);
                }
                const raw = item[column.key];
                if (raw == null) return '';
                if (typeof raw === 'object') return '';
                return String(raw);
              })()}
            >
              {column.render ? (
                column.render(item[column.key], item, index)
              ) : (() => {
                const raw = item[column.key];
                if (raw === null || raw === undefined || raw === '') return '-';
                // If the value is an object (related field), try to display a friendly property
                if (typeof raw === 'object') {
                  // common display fields to try
                  const candidates = ['nombre', 'nombre_completo', 'name', 'label', 'display', 'titulo', 'descripcion'];
                  for (const c of candidates) {
                    if (raw[c]) return raw[c];
                  }
                  // If object has 'id' and another descriptive field like 'pk' or similar, try to humanize keys
                  const firstVal = Object.values(raw).find(v => typeof v === 'string' || typeof v === 'number');
                  if (firstVal !== undefined) return String(firstVal);
                  return JSON.stringify(raw);
                }
                return String(raw);
              })()
              }
            </TableCell>
          ))}
          {/* Columna de acciones */}
          {(allowView || allowEdit || allowDelete) && (
            <TableCell $variant={tableVisualVariant} style={{ textAlign: 'center' }}>
              <RowActionsContainer>
                {allowView && onView && (
                  <RowActionButton
                    $variant="view"
                    $tableVariant={tableVisualVariant}
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(item, index);
                    }}
                    title="Ver perfil"
                  >
                    <i className="bi bi-eye"></i>
                  </RowActionButton>
                )}
                {allowEdit && onEdit && (
                  <RowActionButton
                    $variant="edit"
                    $tableVariant={tableVisualVariant}
                    onClick={(e) => {
                      e.stopPropagation(); // Evitar que se active el onClick de la fila
                      onEdit(item, index);
                    }}
                    title="Editar"
                  >
                    <i className="bi bi-pencil"></i> {/* Bootstrap icon para editar */}
                  </RowActionButton>
                )}
                {allowDelete && (
                  <RowActionButton
                    $variant="delete"
                    $tableVariant={tableVisualVariant}
                    onClick={async (e) => {
                      e.stopPropagation(); // Evitar que se active el onClick de la fila
                      console.debug('ListaUnificada: delete button clicked for', item && item.id);
                      try {
                        // Helper that actually performs the API delete and refreshes the list.
                        let deletedByPage = false;
                        const performDelete = async () => {
                          if (deletedByPage) return;
                          deletedByPage = true;
                          if (endpoint && item && item.id) {
                            try {
                              // El endpoint puede traer querystring (p.ej. 'check-lists/?equipo=123'
                              // para listar filtrado); el id del recurso va sobre el path, nunca
                              // después de los query params, o la URL de borrado queda inválida.
                              const [basePath] = endpoint.split('?');
                              let deleteEndpoint = basePath;
                              if (!deleteEndpoint.endsWith('/')) deleteEndpoint += '/';
                              deleteEndpoint = `${deleteEndpoint}${item.id}/`;
                              console.debug('ListaUnificada: performDelete -> calling apiClient.delete', deleteEndpoint);
                              await apiClient.delete(deleteEndpoint);
                              console.debug('ListaUnificada: performDelete -> apiClient.delete succeeded for', item && item.id);
                            } catch (delErr) {
                              console.error('ListaUnificada: performDelete -> api delete failed', delErr);
                              throw delErr;
                            }
                          } else {
                            console.warn('ListaUnificada: performDelete -> no endpoint or item id');
                            throw new Error('No endpoint or item id');
                          }

                          try {
                            await fetchData();
                            console.debug('ListaUnificada: performDelete -> fetchData completed after delete');
                          } catch (fdErr) {
                            console.warn('ListaUnificada: performDelete -> fetchData failed after delete', fdErr);
                          }
                        };

                        if (onDelete) {
                          // Give the page the chance to ask confirmation and call performDelete when ready.
                          try {
                            console.debug('ListaUnificada: calling onDelete prop with performDelete helper');
                            const res = onDelete(item, index, performDelete);

                            // If the page returned a Promise, await it. If it resolves truthy, assume page handled the delete.
                            if (res && typeof res.then === 'function') {
                              const val = await res;
                              if (!val && !deletedByPage) {
                                console.debug('ListaUnificada: onDelete promise resolved falsy and did not call performDelete; performing fallback delete');
                                await performDelete();
                              } else {
                                console.debug('ListaUnificada: onDelete promise resolved truthy or already deleted by page');
                              }
                            } else if (res === true) {
                              console.debug('ListaUnificada: onDelete returned true (handled)');
                            } else {
                              // Synchronous undefined return: wait briefly to let page call performDelete after confirmation UI.
                              await new Promise(r => setTimeout(r, 600));
                              if (!deletedByPage) {
                                console.debug('ListaUnificada: onDelete did not call performDelete within timeout; performing fallback delete');
                                await performDelete();
                              }
                            }
                          } catch (errPage) {
                            console.warn('ListaUnificada: onDelete handler threw/rejected; performing fallback delete', errPage);
                            if (!deletedByPage) await performDelete();
                          }
                        } else {
                          // No page handler: perform delete directly
                          await performDelete();
                        }
                      } catch (err) {
                        console.error('ListaUnificada: error eliminando elemento:', err);
                        toast.error('Error al eliminar elemento');
                      }
                    }}
                    title="Eliminar"
                  >
                    <i className="bi bi-trash"></i> {/* Bootstrap icon para eliminar */}
                  </RowActionButton>
                )}
              </RowActionsContainer>
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
  
  const renderPagination = () => {
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        perPageOptions={itemsPerPageOptions}
        onPageChange={handlePageChange}
        onPerPageChange={handleItemsPerPageChange}
        itemLabel="elementos"
        showPerPageSelector
        showTotalCounter
        showPageInfo
      />
    );
  };
  
  const renderFiltersPanel = () => (
    <FiltersPanel $show={showFilters} ref={filtersRef}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h6 style={{ margin: 0 }}>Filtros</h6>
        <ActionButton $variant="secondary" onClick={handleClearFilters}>
          <i className="fas fa-times"></i>
          Limpiar
        </ActionButton>
      </div>
      
      {filterConfig.map((field, index) => (
        <FilterRow key={field.key || index}>
          <FilterLabel>{field.label}:</FilterLabel>
          {field.type === 'date' ? (
            <FilterInput
              type="date"
              value={filters[field.key] || ''}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
            />
          ) : field.type === 'select' ? (
            <div style={{ flex: 1, minWidth: 250 }}>
              <SearchableSelectField
                options={(field.options || []).map((option) => ({
                  value: String(option.value ?? ''),
                  label: option.label,
                }))}
                value={filters[field.key] || ''}
                onChange={(value) => handleFilterChange(field.key, value)}
                placeholder="Todos"
                showAllOnFocus
                size="sm"
              />
            </div>
          ) : (
            <FilterInput
              type="text"
              placeholder={field.placeholder}
              value={filters[field.key] || ''}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
            />
          )}
        </FilterRow>
      ))}
    </FiltersPanel>
  );

  const renderAdvancedFiltersModal = () => {
    if (!useAdvancedFilters || !showFiltersModal) return null;
    const dateFilters = (filterConfig || []).filter((f) => f?.type === 'date');
    return (
      <Modal title="Filtros" onClose={() => setShowFiltersModal(false)} size="md" maxHeight="90vh">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
          <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, paddingRight: 4 }}>
            <DynamicFilterBuilder
              title="Filtrar por columnas"
              rows={data}
              columns={advancedFilterColumns}
              value={dynamicFilters}
              onChange={setDynamicFilters}
            />
          </div>

          {dateFilters.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 8 }}>
              {dateFilters.map((field) => (
                <div key={field.key}>
                  <label style={{ display: 'block', marginBottom: 4 }}>{field.label}</label>
                  <FilterInput
                    type="date"
                    value={filters[field.key] || ''}
                    onChange={(e) => handleFilterChange(field.key, e.target.value)}
                    style={{ minWidth: 0, width: '100%', height: 30 }}
                  />
                </div>
              ))}
            </div>
          )}

        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <ActionButton $variant="secondary" onClick={handleClearFilters}>
            Limpiar filtros
          </ActionButton>
          <ActionButton onClick={() => setShowFiltersModal(false)}>
            Aplicar
          </ActionButton>
        </div>
      </Modal>
    );
  };
  
  // ============================= RENDERIZADO PRINCIPAL =============================
  
  if (error) {
    return (
      <ListaContainer>
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          color: theme.colors.danger,
          background: `${theme.colors.danger}22`,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.danger}`
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
          <h4>Error</h4>
          <p>{error}</p>
          <ActionButton $variant="primary" onClick={() => fetchData()}>
            <i className="fas fa-redo"></i>
            Reintentar
          </ActionButton>
        </div>
      </ListaContainer>
    );
  }
  
  const isRows = tableVisualVariant === 'rows';
  const AddButtonComp = isRows ? RowsGhostButton : ActionButton;
  const addButtonExtraProps = isRows ? {} : { $variant: 'success' };

  return (
  <ListaContainer $sinMarco={sinMarco} $variant={tableVisualVariant} {...props}>
      {/* Encabezado: barra de sección del sistema, o cabecera propia clásica */}
      {barraSeccion ? (
        <SeccionBar>
          <SeccionTitulo>{titulo}</SeccionTitulo>
          <SeccionAcciones>
            <SeccionBuscador
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
            />

            {filterConfig.length > 0 && !useAdvancedFilters && (
              <div style={{ position: 'relative' }}>
                <OutlinedButton
                  $size="sm"
                  style={ESTILO_BOTON_SECCION}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <i className="bi bi-funnel" style={{ fontSize: 14 }} /> Filtros
                  {Object.keys(filters).length > 0 ? ` (${Object.keys(filters).length})` : ''}
                </OutlinedButton>
                {renderFiltersPanel()}
              </div>
            )}

            {filterConfig.length > 0 && useAdvancedFilters && (() => {
              const activeServerFilters = Object.values(filters).filter(v => v !== undefined && v !== '').length;
              const activeDynamicFilters = Object.keys(dynamicFilters.touchedFilters || {}).filter(k => dynamicFilters.touchedFilters[k]).length;
              const totalActive = activeServerFilters + activeDynamicFilters;
              return (
                <OutlinedButton
                  $size="sm"
                  style={ESTILO_BOTON_SECCION}
                  onClick={() => setShowFiltersModal(true)}
                >
                  <i className="bi bi-funnel" style={{ fontSize: 14 }} /> Filtros
                  {totalActive > 0 ? ` (${totalActive})` : ''}
                </OutlinedButton>
              );
            })()}

            {additionalActions.map((action, index) => (
              <OutlinedButton
                key={index}
                $variant={action.variant === 'secondary' ? 'secondary' : 'success'}
                $size="sm"
                style={ESTILO_BOTON_SECCION}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon && <i className={action.icon} />}
                {action.label}
              </OutlinedButton>
            ))}

            {onAdd && allowAdd && (
              <OutlinedButton
                $variant="success"
                $size="sm"
                style={ESTILO_BOTON_SECCION}
                onClick={onAdd}
              >
                <i className="bi bi-plus-circle" /> {textoBotonAgregar}
              </OutlinedButton>
            )}
          </SeccionAcciones>
        </SeccionBar>
      ) : (
        <HeaderBar $variant={tableVisualVariant}>
          <TitleSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PageTitle $variant={tableVisualVariant} $tituloGrande={tituloGrande}>
                <i className={icono}></i>
                {titulo}
              </PageTitle>
              {help && (
                <HelpToggleBtn
                  type="button"
                  $active={helpOpen}
                  onClick={() => setHelpOpen(v => !v)}
                >
                  <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
                  Ayuda
                </HelpToggleBtn>
              )}
            </div>
            {subtitle && (
              <Subtitle>{subtitle}</Subtitle>
            )}
          </TitleSection>
        
          <ControlsSection>
            {/* Búsqueda */}
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                $variant={tableVisualVariant}
              />
              {searchValue && (
                <ClearButton onClick={() => handleSearchChange('')}>
                  <i className="fas fa-times"></i>
                </ClearButton>
              )}
              <SearchIcon>
                <i className="fas fa-search"></i>
              </SearchIcon>
            </SearchContainer>
          
            {/* Botón de filtros */}
            {filterConfig.length > 0 && !useAdvancedFilters && (
              <div style={{ position: 'relative' }}>
                <FilterButton
                  $active={showFilters}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <i className="fas fa-filter"></i>
                  Filtros
                  {Object.keys(filters).length > 0 && ` (${Object.keys(filters).length})`}
                </FilterButton>
                {renderFiltersPanel()}
              </div>
            )}

            {filterConfig.length > 0 && useAdvancedFilters && (() => {
              const activeServerFilters = Object.values(filters).filter(v => v !== undefined && v !== '').length;
              const activeDynamicFilters = Object.keys(dynamicFilters.touchedFilters || {}).filter(k => dynamicFilters.touchedFilters[k]).length;
              const totalActive = activeServerFilters + activeDynamicFilters;
              return (
                <FilterButton
                  $active={showFiltersModal || totalActive > 0}
                  onClick={() => setShowFiltersModal(true)}
                >
                  <i className="fas fa-filter"></i>
                  Filtros
                  {totalActive > 0 && ` (${totalActive})`}
                </FilterButton>
              );
            })()}

            {/* Botones de acción */}
            {onAdd && allowAdd && (
              <AddButtonComp {...addButtonExtraProps} onClick={onAdd}>
                <i className="fas fa-plus"></i>
                Agregar
              </AddButtonComp>
            )}

            {/* Acciones adicionales */}
            {additionalActions.map((action, index) => (
              <ActionButton
                key={index}
                $variant={action.variant || 'secondary'}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon && <i className={action.icon}></i>}
                {action.label}
              </ActionButton>
            ))}
          </ControlsSection>
        </HeaderBar>
      )}

      {help && helpOpen && (
        <div style={{ marginBottom: 10 }}>
          <HelpPanel
            prereqs={help.prereqs}
            tips={help.tips}
            nextSteps={help.nextSteps}
            inlineOpen
          />
        </div>
      )}

      {/* Tabla */}
      <TableContainer $variant={tableVisualVariant}>
        {loading ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            color: theme.colors.textSecondary
          }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>Cargando datos...</p>
          </div>
        ) : data.length === 0 ? (
          emptyState && !searchValue && Object.keys(filters).length === 0 ? (
            emptyState
          ) : (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: theme.colors.textSecondary
            }}>
              <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
              <p>No hay datos para mostrar</p>
              {searchValue && (
                isRows ? (
                  <RowsGhostButton onClick={() => handleSearchChange('')}>
                    <i className="fas fa-times"></i>
                    Limpiar búsqueda
                  </RowsGhostButton>
                ) : (
                  <ActionButton $variant="secondary" onClick={() => handleSearchChange('')}>
                    <i className="fas fa-times"></i>
                    Limpiar búsqueda
                  </ActionButton>
                )
              )}
            </div>
          )
        ) : (
          <Table $variant={tableVisualVariant} $minWidth={tableMinWidth}>
            {renderTableHeader()}
            {renderTableBody()}
          </Table>
        )}
      </TableContainer>
      
      {/* Paginación */}
      {rowsToRender.length > 0 && renderPagination()}
      {renderAdvancedFiltersModal()}
    </ListaContainer>
  );
});

export default ListaUnificada;
